import React, { useEffect, useState, useMemo } from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Info, AlertTriangle } from 'lucide-react';
import { CharacterCountService, CharacterStats } from '@/utils/sms/characterCount';
import { cn } from '@/lib/utils';

interface EnhancedMessageCharacterCountProps {
  message: string;
  organisationId?: string;
  includeOptOut?: boolean;
  className?: string;
  showVariableList?: boolean;
}

const EnhancedMessageCharacterCount: React.FC<EnhancedMessageCharacterCountProps> = ({
  message,
  organisationId,
  includeOptOut = true,
  className,
  showVariableList = false
}) => {
  const [stats, setStats] = useState<CharacterStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [variablesFound, setVariablesFound] = useState<string[]>([]);

  // Create service instance
  const countService = useMemo(() => {
    return new CharacterCountService(organisationId);
  }, [organisationId]);

  // Fetch restaurant data and calculate stats
  useEffect(() => {
    const calculateStats = async () => {
      if (!message) {
        setStats(null);
        setVariablesFound([]);
        return;
      }

      setLoading(true);

      try {
        // Fetch actual restaurant data if organisation provided
        if (organisationId) {
          await countService.fetchVariableLengths(organisationId);
        }

        // Calculate statistics
        const calculatedStats = countService.calculateStats(message, includeOptOut);
        setStats(calculatedStats);

        // Get list of variables in message
        const variables = countService.getVariablesInMessage(message);
        setVariablesFound(variables);

      } catch (error) {
        console.error('Error calculating message statistics:', error);
      } finally {
        setLoading(false);
      }
    };

    // Debounce calculation
    const timer = setTimeout(calculateStats, 300);
    return () => clearTimeout(timer);
  }, [message, organisationId, includeOptOut, countService]);

  // Determine color based on segment count
  const getSegmentColor = (segments: number): string => {
    if (segments <= 1) return 'text-green-600';
    if (segments <= 2) return 'text-amber-600';
    if (segments <= 3) return 'text-orange-600';
    return 'text-red-600';
  };

  // Determine badge variant
  const getBadgeVariant = (segments: number): 'default' | 'secondary' | 'destructive' => {
    if (segments <= 1) return 'default';
    if (segments <= 2) return 'secondary';
    return 'destructive';
  };

  if (loading) {
    return (
      <div className={cn('text-sm text-muted-foreground', className)}>
        Calculating character count...
      </div>
    );
  }

  if (!stats || !message) {
    return null;
  }

  const isSimple = !stats.hasVariables;
  const maxSegments = stats.segments.max;

  return (
    <div className={cn('space-y-2', className)}>
      {/* Main character count display */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">
            {isSimple ? 'Characters:' : 'Character Range:'}
          </span>
          {isSimple ? (
            <span className={cn('text-sm font-medium', getSegmentColor(maxSegments))}>
              {stats.maximum}
            </span>
          ) : (
            <span className={cn('text-sm font-medium', getSegmentColor(maxSegments))}>
              {stats.minimum} - {stats.maximum}
            </span>
          )}
          {includeOptOut && (
            <span className="text-xs text-muted-foreground">
              (includes opt-out link)
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          <Badge variant={getBadgeVariant(maxSegments)}>
            {stats.segments.min === stats.segments.max ? (
              `${stats.segments.max} SMS`
            ) : (
              `${stats.segments.min}-${stats.segments.max} SMS`
            )}
          </Badge>
          {stats.hasUnicode && (
            <Badge variant="outline" className="text-xs">
              Unicode
            </Badge>
          )}
        </div>
      </div>

      {/* Variable expansion info */}
      {stats.hasVariables && (
        <div className="text-xs text-muted-foreground space-y-1">
          <div className="flex items-start gap-1">
            <Info className="h-3 w-3 mt-0.5 flex-shrink-0" />
            <span>
              Character count varies based on customer data and restaurant information.
              {organisationId
                ? ' Estimates based on your actual restaurant URLs.'
                : ' Using default estimates for URLs and names.'}
            </span>
          </div>

          {showVariableList && variablesFound.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1">
              <span className="text-muted-foreground">Variables:</span>
              {variablesFound.map((variable) => (
                <Badge key={variable} variant="outline" className="text-xs">
                  {variable}
                </Badge>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Warning for high segment count */}
      {maxSegments > 2 && (
        <Alert className="py-2">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription className="text-xs">
            Message will be split into {stats.segments.min === stats.segments.max
              ? `${stats.segments.max} parts`
              : `${stats.segments.min}-${stats.segments.max} parts`
            }. Consider shortening to reduce costs.
          </AlertDescription>
        </Alert>
      )}

      {/* Cost estimate */}
      {maxSegments > 1 && (
        <div className="text-xs text-muted-foreground">
          <span className="font-medium">Cost impact:</span> Each recipient will be charged for {
            stats.segments.min === stats.segments.max
              ? `${stats.segments.max} message${stats.segments.max > 1 ? 's' : ''}`
              : `${stats.segments.min}-${stats.segments.max} messages`
          }
        </div>
      )}
    </div>
  );
};

export default EnhancedMessageCharacterCount;